import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { GovernanceIssuesService, type GovernanceIssueState } from './GovernanceIssuesService';
import { issueKey, type IssueKind, type IssueStatus } from './PolicyIssues';

/**
 * Durable, team-shared triage state for governance issues — status, flag, and assignee —
 * loaded from the backend and keyed by issue key (`${kind}:${ref}`). A DI singleton so the
 * Issues tab and the Issues badge share one source of truth and re-render together.
 *
 * List-level state (status/flag/assignee/resolution) lives here; per-issue detail (notes,
 * audit trail, dismissed turns) is loaded on demand via {@link GovernanceIssuesService}.
 * Reads are synchronous off the in-memory cache; writes call the service then refresh it.
 */
@injectable()
export class GovernanceIssueStore extends BaseViewModel {
    private states = new Map<string, GovernanceIssueState>();
    private loadedOnce = false;
    loading = false;

    constructor(private readonly service: GovernanceIssuesService) {
        super();
    }

    /** Load all tracked issue states once. Safe to call repeatedly; use {@link reload} to force. */
    async initialize(): Promise<void> {
        if (this.loadedOnce || this.loading) return;
        await this.reload();
    }

    async reload(): Promise<void> {
        this.loading = true;
        this.notifyChange();
        try {
            const states = await this.service.list();
            this.states = new Map(states.map((s) => [issueKey(s.kind as IssueKind, s.ref), s]));
            this.loadedOnce = true;
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    statusOf(key: string): IssueStatus {
        return (this.states.get(key)?.status as IssueStatus | undefined) ?? 'open';
    }

    isFlagged(key: string): boolean {
        return this.states.get(key)?.flagged ?? false;
    }

    assigneeOf(key: string): string | null {
        return this.states.get(key)?.assigneeUserId ?? null;
    }

    async setStatus(kind: IssueKind, ref: string, status: IssueStatus): Promise<void> {
        const detail = await this.service.update(kind, ref, { status });
        this.apply(kind, ref, detail);
    }

    async setFlag(kind: IssueKind, ref: string, flagged: boolean): Promise<void> {
        const detail = await this.service.update(kind, ref, { flagged });
        this.apply(kind, ref, detail);
    }

    async assign(kind: IssueKind, ref: string, assigneeUserId: string | null): Promise<void> {
        const detail = await this.service.update(kind, ref, { assigneeUserId });
        this.apply(kind, ref, detail);
    }

    async resolve(kind: IssueKind, ref: string, reason: string): Promise<void> {
        const detail = await this.service.resolve(kind, ref, reason);
        this.apply(kind, ref, detail);
    }

    private apply(kind: IssueKind, ref: string, state: GovernanceIssueState): void {
        this.states.set(issueKey(kind, ref), state);
        this.notifyChange();
    }
}
